import React, { useEffect, useRef, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import RichTextEditor from '@/components/RichTextEditor';
import { toast } from 'sonner';
import {
  Lock, Plus, Trash2, ChevronDown, ChevronUp, ArrowUp, ArrowDown,
  GripVertical, RefreshCw, Car, LifeBuoy, Map, Wallet, Backpack, Bus,
} from 'lucide-react';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const ACTIVITY_TYPES = [
  'pickup', 'dropoff', 'activity', 'meal', 'flight',
  'checkin', 'checkout', 'free', 'other',
];
const PLACE_CATEGORIES = [
  'breakfast', 'lunch', 'dinner', 'cafe', 'bar', 'must_visit', 'shopping', 'other',
];
const PRICE_RANGES = ['budget', 'mid', 'premium'];

// NOTE: intentionally NO hardcoded Travcations number here (see 2026-08-17
// "Dynamic PDF Footer" fix — a stale "+91 98368 88368" was hardcoded here
// too and must never come back). The agent fills this row in manually per
// itinerary, same as the other rows below.
const DEFAULT_EMERGENCY = [
  { label: 'Local Police', number: '', notes: '' },
  { label: 'Nearest Hospital', number: '', notes: '' },
  { label: 'Travcations 24hr Helpline', number: '', notes: '' },
  { label: 'Indian Embassy', number: '', notes: '' },
];

// Predefined "things to carry" categories + destination-aware item suggestions.
const CARRY_CATEGORY_SUGGESTIONS = {
  beach: {
    Documents: ['Passport / Govt ID', 'Travel insurance', 'Printed vouchers'],
    Clothing: ['Swimwear', 'Light cottons', 'Beach towel', 'Cap / hat'],
    Electronics: ['Phone & charger', 'Power bank', 'Universal adapter'],
    'Personal Care': ['Sunscreen SPF 50+', 'Sunglasses', 'Mosquito repellent'],
  },
  mountain: {
    Documents: ['Passport / Govt ID', 'Travel insurance', 'Printed vouchers'],
    Clothing: ['Warm jacket', 'Thermals', 'Trekking shoes', 'Gloves & beanie'],
    Electronics: ['Phone & charger', 'Power bank', 'Universal adapter'],
    'Personal Care': ['Moisturizer', 'Lip balm', 'First-aid kit', 'Sunscreen'],
  },
  city: {
    Documents: ['Passport / Govt ID', 'Travel insurance', 'Printed vouchers'],
    Clothing: ['Comfortable walking shoes', 'Layered clothing', 'Light jacket'],
    Electronics: ['Phone & charger', 'Power bank', 'Universal adapter'],
    'Personal Care': ['Toiletries', 'Hand sanitizer', 'Umbrella'],
  },
};
const CARRY_DEFAULT_CATEGORIES = ['Documents', 'Clothing', 'Electronics', 'Personal Care'];

function inferDestinationType(destination = '') {
  const d = String(destination).toLowerCase();
  if (/(beach|island|maldives|goa|bali|phuket|andaman|lakshadweep|seychelles|mauritius|krabi|langkawi)/.test(d)) return 'beach';
  if (/(mountain|hill|trek|ladakh|himalaya|manali|shimla|spiti|alps|kashmir|switzerland|nepal|leh|sikkim)/.test(d)) return 'mountain';
  return 'city';
}

// Shared borderless (bottom-border only) field styles
const V_INPUT = 'w-full bg-transparent border-0 border-b border-slate-200 rounded-none px-0 py-1.5 text-sm text-slate-800 focus:outline-none focus:border-primary-500 placeholder:text-slate-300 min-h-[44px] md:min-h-0';

// On phones, every day accordion starts collapsed to reduce scroll. On
// desktop the first day stays expanded (existing behaviour).
const IS_MOBILE = typeof window !== 'undefined' && window.innerWidth < 768;
const V_SELECT = `${V_INPUT} cursor-pointer`;

// Coerce any incoming time format into the strict "HH:MM" 24h string that a
// native <input type="time"> requires. Anything it can't parse → "" (blank).
// Without this, legacy / AI-generated / imported values like "6:00", "6:00 AM",
// "0600", "0600h" render BLANK on reopen because the native control rejects
// them — which is the "time shows blank/wrong on reopen" bug.
export function normalizeTimeInput(raw) {
  if (raw == null) return '';
  let s = String(raw).trim();
  if (!s) return '';
  s = s.replace(/h$/i, '').trim();                       // "0600h" / "06:00h"
  const ap = /(am|pm)\s*$/i.exec(s);
  const ampm = ap ? ap[1].toLowerCase() : '';
  if (ampm) s = s.replace(/(am|pm)\s*$/i, '').trim();
  let h; let m;
  const colon = /^(\d{1,2}):(\d{2})$/.exec(s);           // "6:00" / "06:00" / "18:30"
  if (colon) {
    h = parseInt(colon[1], 10); m = parseInt(colon[2], 10);
  } else {
    const digits = s.replace(/\D/g, '');
    if (digits.length === 4) { h = parseInt(digits.slice(0, 2), 10); m = parseInt(digits.slice(2), 10); } // "0600"
    else if (digits.length === 3) { h = parseInt(digits.slice(0, 1), 10); m = parseInt(digits.slice(1), 10); } // "600"
    else if (digits.length >= 1 && digits.length <= 2) { h = parseInt(digits, 10); m = 0; } // "6"
    else return '';
  }
  if (Number.isNaN(h) || Number.isNaN(m)) return '';
  if (ampm === 'pm' && h < 12) h += 12;
  if (ampm === 'am' && h === 12) h = 0;
  if (h < 0 || h > 23 || m < 0 || m > 59) return '';
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

// Friendly 12-hour label for a stored time. HH:MM → "9:00 AM"; any other
// (non-HH:MM) value is shown verbatim so the agent never loses what they typed.
export function to12hLabel(value) {
  const m = /^(\d{1,2}):(\d{2})$/.exec(String(value || '').trim());
  if (!m) return value ? String(value) : '';
  let h = parseInt(m[1], 10);
  const ap = h >= 12 ? 'PM' : 'AM';
  h = h % 12 || 12;
  return `${h}:${m[2]} ${ap}`;
}

// Free-text time field. Accepts ANY format the agent types ("9", "9:00 AM",
// "0930", "14:30", "6 pm") and normalises to a clean HH:MM on blur. We avoid
// the native <input type="time"> because it SILENTLY BLANKS any value that
// isn't already strict "HH:MM" and logs an "invalid value / does not conform to
// the required format 'HH:mm'" error — the exact "times not accepted / invalid
// value" bug. As a text input it can never throw that, and unparseable text is
// kept verbatim (the client voucher's fmtTime falls back to raw display).
const TimeTextInput = ({ value, onChange, className, testId, placeholder = 'e.g. 9:00 AM' }) => {
  const [draft, setDraft] = useState(null); // null → show the formatted value
  const display = draft != null ? draft : to12hLabel(value);
  const commit = () => {
    if (draft != null) {
      const raw = draft.trim();
      const norm = normalizeTimeInput(raw);
      onChange(norm || raw); // keep raw text if unparseable — never lose input
    }
    setDraft(null);
  };
  return (
    <input
      type="text"
      value={display}
      placeholder={placeholder}
      onFocus={() => setDraft(to12hLabel(value))}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); } }}
      className={className}
      data-testid={testId}
    />
  );
};

// Build the 4 predefined categories pre-filled with destination-aware items.
function defaultCarryCategories(destination) {
  const sug = CARRY_CATEGORY_SUGGESTIONS[inferDestinationType(destination)] || {};
  return CARRY_DEFAULT_CATEGORIES.map((name) => ({ name, items: [...(sug[name] || [])] }));
}

// Coerce any stored things_to_carry shape into [{ name, items: [] }].
// Legacy flat string lists collapse into a single "Essentials" category.
function normalizeCarry(value) {
  if (!Array.isArray(value) || value.length === 0) return [];
  if (typeof value[0] === 'string') return [{ name: 'Essentials', items: value.filter(Boolean) }];
  return value.map((c) => ({ name: c.name || '', items: Array.isArray(c.items) ? c.items : [] }));
}

// One editable category card: rename, add/remove items, remove category.
const CarryCategory = ({ idx, name, items, onRename, onAddItem, onRemoveItem, onRemoveCategory }) => {
  const [draft, setDraft] = useState('');
  const list = Array.isArray(items) ? items : [];
  const add = () => { const v = draft.trim(); if (!v) return; onAddItem(v); setDraft(''); };
  return (
    <div className="rounded-lg border border-slate-200 p-3" data-testid={`carry-category-${idx}`}>
      <div className="flex items-center gap-2 mb-2">
        <input
          value={name}
          onChange={(e) => onRename(e.target.value)}
          placeholder="Category name"
          className="flex-1 font-medium text-sm text-slate-800 bg-transparent border-0 border-b border-transparent hover:border-slate-200 focus:border-primary-500 focus:outline-none"
          data-testid={`carry-category-name-${idx}`}
        />
        <button type="button" onClick={onRemoveCategory} className="text-slate-300 hover:text-red-500" data-testid={`carry-category-remove-${idx}`}>
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
      <div className="flex flex-wrap gap-2 mb-2">
        {list.map((t, ii) => (
          <span key={ii} className="inline-flex items-center gap-1.5 bg-primary-50 text-primary-800 text-xs px-2.5 py-1 rounded-full" data-testid={`carry-item-${idx}-${ii}`}>
            {t}
            <button type="button" onClick={() => onRemoveItem(ii)} className="text-primary-400 hover:text-primary-700" data-testid={`carry-item-remove-${idx}-${ii}`}>×</button>
          </span>
        ))}
      </div>
      <input
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); add(); } }}
        placeholder="Add an item and press Enter"
        className={V_INPUT}
        data-testid={`carry-item-input-${idx}`}
      />
    </div>
  );
};

// ---------------------------------------------------------------------------
// Small primitives
// ---------------------------------------------------------------------------
const SectionDivider = ({ icon: Icon, children }) => (
  <div className="flex items-center gap-3 mt-9 mb-4">
    {Icon && <Icon className="h-3.5 w-3.5 text-slate-400" strokeWidth={2} />}
    <span className="text-[10px] font-semibold uppercase tracking-[0.14em] text-slate-400 whitespace-nowrap">
      {children}
    </span>
    <span className="flex-1 h-px bg-slate-200" />
  </div>
);

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] font-semibold uppercase tracking-[0.1em] text-slate-400 block mb-1">{label}</label>
    {children}
  </div>
);

// ---------------------------------------------------------------------------
// Per-day activity row
// ---------------------------------------------------------------------------
const ActivityRow = ({ act, dayIdx, actIdx, count, onChange, onRemove, onMove }) => (
  <div
    className="grid grid-cols-12 gap-2 items-end py-2 border-b border-dashed border-slate-100"
    data-testid={`activity-row-${dayIdx}-${actIdx}`}
  >
    <div className="col-span-1 flex items-center justify-center pb-1.5 text-slate-300">
      <GripVertical className="h-4 w-4" />
    </div>
    <div className="col-span-2">
      <Field label="Time">
        <TimeTextInput
          value={act.time}
          onChange={(v) => onChange(actIdx, 'time', v)}
          className={V_INPUT}
          testId={`activity-time-${dayIdx}-${actIdx}`}
        />
      </Field>
    </div>
    <div className="col-span-3">
      <Field label="Title">
        <input
          value={act.title || ''}
          onChange={(e) => onChange(actIdx, 'title', e.target.value)}
          placeholder="e.g. Airport pickup"
          className={V_INPUT}
          data-testid={`activity-title-${dayIdx}-${actIdx}`}
        />
      </Field>
    </div>
    <div className="col-span-2">
      <Field label="Type">
        <select
          value={act.activity_type || 'activity'}
          onChange={(e) => onChange(actIdx, 'activity_type', e.target.value)}
          className={V_SELECT}
          data-testid={`activity-type-${dayIdx}-${actIdx}`}
        >
          {ACTIVITY_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </Field>
    </div>
    <div className="col-span-2">
      <Field label="Location">
        <input
          value={act.location || ''}
          onChange={(e) => onChange(actIdx, 'location', e.target.value)}
          placeholder="Place"
          className={V_INPUT}
          data-testid={`activity-location-${dayIdx}-${actIdx}`}
        />
      </Field>
    </div>
    <div className="col-span-1">
      <Field label="Mins">
        <input
          type="number"
          min="0"
          value={act.duration_mins ?? ''}
          onChange={(e) => onChange(actIdx, 'duration_mins', e.target.value === '' ? null : Number(e.target.value))}
          className={V_INPUT}
          data-testid={`activity-duration-${dayIdx}-${actIdx}`}
        />
      </Field>
    </div>
    <div className="col-span-1 flex items-center justify-end gap-0.5 pb-1">
      <button type="button" onClick={() => onMove(actIdx, -1)} disabled={actIdx === 0}
        className="text-slate-300 hover:text-slate-600 disabled:opacity-30 p-0.5" title="Move up">
        <ArrowUp className="h-3.5 w-3.5" />
      </button>
      <button type="button" onClick={() => onMove(actIdx, 1)} disabled={actIdx === count - 1}
        className="text-slate-300 hover:text-slate-600 disabled:opacity-30 p-0.5" title="Move down">
        <ArrowDown className="h-3.5 w-3.5" />
      </button>
      <button type="button" onClick={() => onRemove(actIdx)}
        className="text-red-300 hover:text-red-500 p-0.5" data-testid={`activity-remove-${dayIdx}-${actIdx}`} title="Remove">
        <Trash2 className="h-3.5 w-3.5" />
      </button>
    </div>
    <div className="col-span-12 pl-9">
      <input
        value={act.description || ''}
        onChange={(e) => onChange(actIdx, 'description', e.target.value)}
        placeholder="Optional description / note"
        className={V_INPUT}
        data-testid={`activity-desc-${dayIdx}-${actIdx}`}
      />
    </div>
  </div>
);

// ---------------------------------------------------------------------------
// Per-day block (activities + vehicle)
// ---------------------------------------------------------------------------
const DayVoucherBlock = ({ day, dayIdx, defaultExpanded, onUpdateDay }) => {
  const [open, setOpen] = useState(defaultExpanded);
  const activities = Array.isArray(day.activities) ? day.activities : [];
  const vehicle = day.vehicle || null;

  const setActivities = (next) => onUpdateDay(dayIdx, 'activities', next);

  const addActivity = () => setActivities([
    ...activities,
    { time: '', title: '', activity_type: 'activity', location: '', duration_mins: null, description: '' },
  ]);
  const changeActivity = (j, field, value) =>
    setActivities(activities.map((a, i) => (i === j ? { ...a, [field]: value } : a)));
  const removeActivity = (j) => setActivities(activities.filter((_, i) => i !== j));
  const moveActivity = (j, dir) => {
    const k = j + dir;
    if (k < 0 || k >= activities.length) return;
    const next = [...activities];
    [next[j], next[k]] = [next[k], next[j]];
    setActivities(next);
  };

  const toggleVehicle = (on) => onUpdateDay(dayIdx, 'vehicle', on ? (vehicle || {}) : null);
  const changeVehicle = (field, value) => onUpdateDay(dayIdx, 'vehicle', { ...(vehicle || {}), [field]: value });

  return (
    <div className="border border-slate-150 rounded-xl overflow-hidden bg-white" data-testid={`voucher-day-block-${dayIdx}`}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-4 py-3 bg-slate-50 hover:bg-slate-100 transition-colors"
        data-testid={`voucher-day-toggle-${dayIdx}`}
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-slate-700">
          <span className="w-6 h-6 rounded-md bg-primary-700 text-white text-xs flex items-center justify-center">{day.dayNumber}</span>
          {day.title || `Day ${day.dayNumber}`}
          {activities.length > 0 && <span className="text-[11px] font-normal text-slate-400">· {activities.length} activity</span>}
          {vehicle && <Car className="h-3.5 w-3.5 text-primary-600" />}
        </span>
        {open ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
      </button>

      {open && (
        <div className="px-4 py-4 border-t border-slate-100">
          {activities.map((act, j) => (
            <ActivityRow
              key={j}
              act={act}
              dayIdx={dayIdx}
              actIdx={j}
              count={activities.length}
              onChange={changeActivity}
              onRemove={removeActivity}
              onMove={moveActivity}
            />
          ))}
          <Button
            type="button" variant="ghost" size="sm"
            onClick={addActivity}
            className="text-primary-700 text-xs mt-2"
            data-testid={`add-activity-${dayIdx}`}
          >
            <Plus className="h-3.5 w-3.5 mr-1" /> Add activity
          </Button>

          {/* Vehicle */}
          <div className="mt-5 pt-4 border-t border-dashed border-slate-150">
            <label className="flex items-center gap-2.5 cursor-pointer">
              <Switch
                checked={!!vehicle}
                onCheckedChange={toggleVehicle}
                data-testid={`vehicle-toggle-${dayIdx}`}
              />
              <span className="text-sm font-medium text-slate-600 flex items-center gap-1.5">
                <Car className="h-4 w-4 text-slate-400" /> Add vehicle for this day
              </span>
            </label>
            {vehicle && (
              <div className="grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-3 mt-4" data-testid={`vehicle-fields-${dayIdx}`}>
                <Field label="Vehicle type">
                  <input value={vehicle.type || ''} onChange={(e) => changeVehicle('type', e.target.value)} placeholder="Sedan / SUV" className={V_INPUT} data-testid={`vehicle-type-${dayIdx}`} />
                </Field>
                <Field label="Number plate">
                  <input value={vehicle.number_plate || ''} onChange={(e) => changeVehicle('number_plate', e.target.value)} className={V_INPUT} data-testid={`vehicle-plate-${dayIdx}`} />
                </Field>
                <Field label="Driver name">
                  <input value={vehicle.driver_name || ''} onChange={(e) => changeVehicle('driver_name', e.target.value)} className={V_INPUT} data-testid={`vehicle-driver-${dayIdx}`} />
                </Field>
                <Field label="Driver phone">
                  <input value={vehicle.driver_phone || ''} onChange={(e) => changeVehicle('driver_phone', e.target.value)} className={V_INPUT} data-testid={`vehicle-driver-phone-${dayIdx}`} />
                </Field>
                <Field label="Pickup point">
                  <input value={vehicle.pickup_point || ''} onChange={(e) => changeVehicle('pickup_point', e.target.value)} className={V_INPUT} data-testid={`vehicle-pickup-${dayIdx}`} />
                </Field>
                <Field label="Pickup time">
                  <TimeTextInput value={vehicle.pickup_time} onChange={(v) => changeVehicle('pickup_time', v)} className={V_INPUT} testId={`vehicle-pickup-time-${dayIdx}`} />
                </Field>
                <div className="col-span-2 md:col-span-3">
                  <Field label="Notes">
                    <input value={vehicle.notes || ''} onChange={(e) => changeVehicle('notes', e.target.value)} className={V_INPUT} data-testid={`vehicle-notes-${dayIdx}`} />
                  </Field>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------
export default function VoucherTab({
  destination,
  voucherStatus, setVoucherStatus,
  bookingRef, setBookingRef,
  coordinator, setCoordinator,
  emergencyContacts, setEmergencyContacts,
  days, setDays,
  hotels, setHotels,
  thingsToCarry, setThingsToCarry,
  transportOptions, setTransportOptions,
  recommendedPlaces, setRecommendedPlaces,
  localCurrency, setLocalCurrency,
  voucherNotes, setVoucherNotes,
}) {
  const [unlocked, setUnlocked] = useState(!!voucherStatus);
  const seededRef = useRef(false);
  useEffect(() => { if (voucherStatus) setUnlocked(true); }, [voucherStatus]);

  // Pre-fill sensible defaults ONCE when the section is first opened and the
  // arrays are still empty (never clobbers saved data).
  useEffect(() => {
    if (!unlocked || seededRef.current) return;
    seededRef.current = true;
    if (!Array.isArray(emergencyContacts) || emergencyContacts.length === 0) {
      setEmergencyContacts(DEFAULT_EMERGENCY.map((c) => ({ ...c })));
    }
    if (!Array.isArray(thingsToCarry) || thingsToCarry.length === 0) {
      setThingsToCarry(defaultCarryCategories(destination));
    } else if (typeof thingsToCarry[0] === 'string') {
      // Legacy flat list → single "Essentials" category (no data loss).
      setThingsToCarry([{ name: 'Essentials', items: thingsToCarry.filter(Boolean) }]);
    }
  }, [unlocked]); // eslint-disable-line react-hooks/exhaustive-deps

  const coord = coordinator || {};
  const setCoord = (field, value) => setCoordinator({ ...coord, [field]: value });
  const cur = localCurrency || {};
  const setCur = (field, value) => setLocalCurrency({ ...cur, [field]: value });

  // Live FX — keyless open.er-api.com. base = local currency code → rates.INR
  // is exactly "₹ per 1 unit" of that currency, which is what the field stores.
  const [fetchingRate, setFetchingRate] = useState(false);
  const fetchRate = async () => {
    const code = (cur.currency_code || '').trim().toUpperCase();
    if (!code) { toast.error('Enter the 3-letter currency code first (e.g. MVR)'); return; }
    setFetchingRate(true);
    try {
      const res = await fetch(`https://open.er-api.com/v6/latest/${encodeURIComponent(code)}`);
      const data = await res.json();
      const inr = data && data.result === 'success' && data.rates ? data.rates.INR : null;
      if (inr != null) {
        const rate = Math.round(Number(inr) * 10000) / 10000;
        setCur('exchange_rate', rate);
        const asOf = (data.time_last_update_utc || '').slice(0, 16) || 'today';
        toast.success(`1 ${code} = ₹${rate} · as of ${asOf}`);
      } else {
        toast.error(`No INR rate found for "${code}". Check the currency code.`);
      }
    } catch {
      toast.error('Exchange-rate service unavailable. Please try again.');
    } finally {
      setFetchingRate(false);
    }
  };

  const updateDay = (idx, field, value) =>
    setDays((prev) => prev.map((d, i) => (i === idx ? { ...d, [field]: value } : d)));

  // ----- Emergency contacts -----
  const ec = Array.isArray(emergencyContacts) ? emergencyContacts : [];
  const addContact = () => setEmergencyContacts([...ec, { label: '', number: '', notes: '' }]);
  const changeContact = (i, field, value) => setEmergencyContacts(ec.map((c, j) => (j === i ? { ...c, [field]: value } : c)));
  const removeContact = (i) => setEmergencyContacts(ec.filter((_, j) => j !== i));

  // ----- Things to carry (categorised) -----
  const carryCats = normalizeCarry(thingsToCarry);
  const setCats = (next) => setThingsToCarry(next);
  const addCategory = () => setCats([...carryCats, { name: '', items: [] }]);
  const removeCategory = (ci) => setCats(carryCats.filter((_, j) => j !== ci));
  const renameCategory = (ci, name) => setCats(carryCats.map((c, j) => (j === ci ? { ...c, name } : c)));
  const addCarryItem = (ci, val) => {
    const v = (val || '').trim();
    setCats(carryCats.map((c, j) => (j === ci && !(c.items || []).includes(v) ? { ...c, items: [...(c.items || []), v] } : c)));
  };
  const removeCarryItem = (ci, ii) => setCats(carryCats.map((c, j) => (j === ci ? { ...c, items: (c.items || []).filter((_, k) => k !== ii) } : c)));

  // ----- Transport options -----
  const to = Array.isArray(transportOptions) ? transportOptions : [];
  const addTransport = () => setTransportOptions([...to, { mode: '', description: '', approx_cost: '', available: true }]);
  const changeTransport = (i, field, value) => setTransportOptions(to.map((t, j) => (j === i ? { ...t, [field]: value } : t)));
  const removeTransport = (i) => setTransportOptions(to.filter((_, j) => j !== i));

  // ----- Recommended places -----
  const rp = Array.isArray(recommendedPlaces) ? recommendedPlaces : [];
  const addPlace = () => setRecommendedPlaces([...rp, { name: '', category: 'must_visit', description: '', address: '', price_range: '', notes: '' }]);
  const changePlace = (i, field, value) => setRecommendedPlaces(rp.map((p, j) => (j === i ? { ...p, [field]: value } : p)));
  const removePlace = (i) => setRecommendedPlaces(rp.filter((_, j) => j !== i));

  // ----- Accommodation confirmations -----
  const htls = Array.isArray(hotels) ? hotels : [];
  const changeHotel = (i, field, value) => setHotels(htls.map((h, j) => (j === i ? { ...h, [field]: value } : h)));

  if (!unlocked) {
    return (
      <div className="text-center py-10" data-testid="voucher-locked">
        <div className="w-14 h-14 rounded-2xl bg-slate-100 mx-auto flex items-center justify-center mb-4">
          <Lock className="h-6 w-6 text-slate-400" />
        </div>
        <p className="text-sm font-medium text-slate-600" title="Available after booking confirmed">
          Available after booking confirmed
        </p>
        <p className="text-xs text-slate-400 mt-1">Travel vouchers carry confirmed booking details for the trip.</p>
        <button
          type="button"
          onClick={() => setUnlocked(true)}
          className="text-xs text-primary-700 underline mt-3"
          data-testid="build-voucher-anyway-btn"
        >
          Build voucher anyway
        </button>
      </div>
    );
  }

  return (
    <div data-testid="voucher-tab">
      {/* BOOKING */}
      <SectionDivider>Booking</SectionDivider>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
        <Field label="Booking reference">
          <input value={bookingRef || ''} onChange={(e) => setBookingRef(e.target.value)} placeholder="e.g. BK-2026-00123" className={V_INPUT} data-testid="voucher-booking-ref" />
        </Field>
        <Field label="Voucher status">
          <select value={voucherStatus || 'draft'} onChange={(e) => setVoucherStatus(e.target.value)} className={V_SELECT} data-testid="voucher-status-select">
            <option value="draft">Draft</option>
            <option value="confirmed">Confirmed</option>
            <option value="sent">Sent</option>
          </select>
        </Field>
      </div>

      {/* COORDINATOR */}
      <SectionDivider icon={LifeBuoy}>Coordinator</SectionDivider>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-x-8 gap-y-3" data-testid="coordinator-section">
        <Field label="Name">
          <input value={coord.name || ''} onChange={(e) => setCoord('name', e.target.value)} className={V_INPUT} data-testid="coordinator-name" />
        </Field>
        <Field label="Phone">
          <input value={coord.phone || ''} onChange={(e) => setCoord('phone', e.target.value)} className={V_INPUT} data-testid="coordinator-phone" />
        </Field>
        <Field label="WhatsApp">
          <input value={coord.whatsapp || ''} onChange={(e) => setCoord('whatsapp', e.target.value)} className={V_INPUT} data-testid="coordinator-whatsapp" />
        </Field>
        <Field label="Email">
          <input value={coord.email || ''} onChange={(e) => setCoord('email', e.target.value)} className={V_INPUT} data-testid="coordinator-email" />
        </Field>
        <Field label="Available hours">
          <input value={coord.available_hours || ''} onChange={(e) => setCoord('available_hours', e.target.value)} placeholder="8AM–10PM IST" className={V_INPUT} data-testid="coordinator-hours" />
        </Field>
      </div>

      {/* EMERGENCY CONTACTS */}
      <SectionDivider>Emergency Contacts</SectionDivider>
      <div className="space-y-2" data-testid="emergency-contacts-section">
        {ec.map((c, i) => (
          <div key={i} className="grid grid-cols-12 gap-3 items-center" data-testid={`emergency-row-${i}`}>
            <input value={c.label || ''} onChange={(e) => changeContact(i, 'label', e.target.value)} placeholder="Label" className={`${V_INPUT} col-span-4`} data-testid={`emergency-label-${i}`} />
            <input value={c.number || ''} onChange={(e) => changeContact(i, 'number', e.target.value)} placeholder="Number" className={`${V_INPUT} col-span-3`} data-testid={`emergency-number-${i}`} />
            <input value={c.notes || ''} onChange={(e) => changeContact(i, 'notes', e.target.value)} placeholder="Notes" className={`${V_INPUT} col-span-4`} data-testid={`emergency-notes-${i}`} />
            <button type="button" onClick={() => removeContact(i)} className="col-span-1 text-red-300 hover:text-red-500 flex justify-end" data-testid={`emergency-remove-${i}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        <Button type="button" variant="ghost" size="sm" onClick={addContact} className="text-primary-700 text-xs mt-1" data-testid="add-emergency-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add contact
        </Button>
      </div>

      {/* PER-DAY ACTIVITIES + VEHICLE */}
      <SectionDivider icon={Map}>Per-day activities & vehicle</SectionDivider>
      <div className="space-y-3" data-testid="per-day-section">
        {(days || []).map((day, i) => (
          <DayVoucherBlock key={i} day={day} dayIdx={i} defaultExpanded={i === 0 && !IS_MOBILE} onUpdateDay={updateDay} />
        ))}
      </div>

      {/* ACCOMMODATION CONFIRMATIONS */}
      {htls.length > 0 && (
        <>
          <SectionDivider>Accommodation Confirmations</SectionDivider>
          <div className="space-y-5" data-testid="accommodation-confirmations-section">
            {htls.map((h, i) => (
              <div key={i} className="rounded-xl border border-slate-150 p-4" data-testid={`hotel-confirm-${i}`}>
                <p className="text-sm font-semibold text-slate-700 mb-3">{h.hotelName || h.name || `Hotel ${i + 1}`}</p>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
                  <Field label="Confirmation number">
                    <input value={h.confirmation_number || ''} onChange={(e) => changeHotel(i, 'confirmation_number', e.target.value)} className={V_INPUT} data-testid={`hotel-confirmation-${i}`} />
                  </Field>
                  <Field label="Hotel phone">
                    <input value={h.hotel_phone || ''} onChange={(e) => changeHotel(i, 'hotel_phone', e.target.value)} className={V_INPUT} data-testid={`hotel-phone-${i}`} />
                  </Field>
                  <Field label="Check-in time">
                    <TimeTextInput value={h.checkin_time} onChange={(v) => changeHotel(i, 'checkin_time', v)} className={V_INPUT} testId={`hotel-checkin-time-${i}`} />
                  </Field>
                  <Field label="Check-out time">
                    <TimeTextInput value={h.checkout_time} onChange={(v) => changeHotel(i, 'checkout_time', v)} className={V_INPUT} testId={`hotel-checkout-time-${i}`} />
                  </Field>
                  <div className="col-span-2 md:col-span-3">
                    <Field label="Hotel address">
                      <input value={h.hotel_address || ''} onChange={(e) => changeHotel(i, 'hotel_address', e.target.value)} className={V_INPUT} data-testid={`hotel-address-${i}`} />
                    </Field>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {/* THINGS TO CARRY (categorised) */}
      <SectionDivider icon={Backpack}>Things to carry</SectionDivider>
      <div className="space-y-3" data-testid="things-to-carry-section">
        {carryCats.map((c, ci) => (
          <CarryCategory
            key={ci}
            idx={ci}
            name={c.name}
            items={c.items}
            onRename={(v) => renameCategory(ci, v)}
            onAddItem={(v) => addCarryItem(ci, v)}
            onRemoveItem={(ii) => removeCarryItem(ci, ii)}
            onRemoveCategory={() => removeCategory(ci)}
          />
        ))}
        <Button type="button" variant="outline" size="sm" onClick={addCategory} className="text-xs" data-testid="carry-add-category-btn">
          <Plus className="h-3.5 w-3.5 mr-1.5" /> Add category
        </Button>
      </div>

      {/* TRANSPORT OPTIONS */}
      <SectionDivider icon={Bus}>Transport options</SectionDivider>
      <div className="space-y-2" data-testid="transport-options-section">
        {to.map((t, i) => (
          <div key={i} className="grid grid-cols-12 gap-3 items-center" data-testid={`transport-row-${i}`}>
            <input value={t.mode || ''} onChange={(e) => changeTransport(i, 'mode', e.target.value)} placeholder="Mode" className={`${V_INPUT} col-span-3`} data-testid={`transport-mode-${i}`} />
            <input value={t.description || ''} onChange={(e) => changeTransport(i, 'description', e.target.value)} placeholder="Description" className={`${V_INPUT} col-span-4`} data-testid={`transport-desc-${i}`} />
            <input value={t.approx_cost || ''} onChange={(e) => changeTransport(i, 'approx_cost', e.target.value)} placeholder="Approx cost" className={`${V_INPUT} col-span-2`} data-testid={`transport-cost-${i}`} />
            <label className="col-span-2 flex items-center gap-2 text-xs text-slate-500">
              <Switch checked={t.available !== false} onCheckedChange={(v) => changeTransport(i, 'available', v)} data-testid={`transport-available-${i}`} />
              Available
            </label>
            <button type="button" onClick={() => removeTransport(i)} className="col-span-1 text-red-300 hover:text-red-500 flex justify-end" data-testid={`transport-remove-${i}`}>
              <Trash2 className="h-4 w-4" />
            </button>
          </div>
        ))}
        <Button type="button" variant="ghost" size="sm" onClick={addTransport} className="text-primary-700 text-xs mt-1" data-testid="add-transport-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add transport option
        </Button>
      </div>

      {/* RECOMMENDED PLACES */}
      <SectionDivider icon={Map}>Recommended places</SectionDivider>
      <div className="space-y-4" data-testid="recommended-places-section">
        {rp.map((p, i) => (
          <div key={i} className="rounded-xl border border-slate-150 p-4" data-testid={`place-row-${i}`}>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-x-6 gap-y-3">
              <Field label="Name">
                <input value={p.name || ''} onChange={(e) => changePlace(i, 'name', e.target.value)} className={V_INPUT} data-testid={`place-name-${i}`} />
              </Field>
              <Field label="Category">
                <select value={p.category || 'must_visit'} onChange={(e) => changePlace(i, 'category', e.target.value)} className={V_SELECT} data-testid={`place-category-${i}`}>
                  {PLACE_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace('_', ' ')}</option>)}
                </select>
              </Field>
              <Field label="Price range">
                <select value={p.price_range || ''} onChange={(e) => changePlace(i, 'price_range', e.target.value)} className={V_SELECT} data-testid={`place-price-${i}`}>
                  <option value="">—</option>
                  {PRICE_RANGES.map((r) => <option key={r} value={r}>{r}</option>)}
                </select>
              </Field>
              <div className="col-span-2 md:col-span-3">
                <Field label="Description">
                  <input value={p.description || ''} onChange={(e) => changePlace(i, 'description', e.target.value)} className={V_INPUT} data-testid={`place-desc-${i}`} />
                </Field>
              </div>
              <div className="col-span-2">
                <Field label="Address">
                  <input value={p.address || ''} onChange={(e) => changePlace(i, 'address', e.target.value)} className={V_INPUT} data-testid={`place-address-${i}`} />
                </Field>
              </div>
              <Field label="Notes">
                <input value={p.notes || ''} onChange={(e) => changePlace(i, 'notes', e.target.value)} className={V_INPUT} data-testid={`place-notes-${i}`} />
              </Field>
            </div>
            <button type="button" onClick={() => removePlace(i)} className="text-red-300 hover:text-red-500 text-xs mt-2 flex items-center gap-1" data-testid={`place-remove-${i}`}>
              <Trash2 className="h-3.5 w-3.5" /> Remove
            </button>
          </div>
        ))}
        <Button type="button" variant="ghost" size="sm" onClick={addPlace} className="text-primary-700 text-xs" data-testid="add-place-btn">
          <Plus className="h-3.5 w-3.5 mr-1" /> Add recommended place
        </Button>
      </div>

      {/* LOCAL CURRENCY */}
      <SectionDivider icon={Wallet}>Local currency</SectionDivider>
      <div data-testid="local-currency-section">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-3 items-end">
          <Field label="Currency name">
            <input value={cur.currency_name || ''} onChange={(e) => setCur('currency_name', e.target.value)} placeholder="Maldivian Rufiyaa" className={V_INPUT} data-testid="currency-name" />
          </Field>
          <Field label="Code">
            <input value={cur.currency_code || ''} onChange={(e) => setCur('currency_code', e.target.value)} placeholder="MVR" className={V_INPUT} data-testid="currency-code" />
          </Field>
          <Field label="Symbol">
            <input value={cur.currency_symbol || ''} onChange={(e) => setCur('currency_symbol', e.target.value)} placeholder=".ރ" className={V_INPUT} data-testid="currency-symbol" />
          </Field>
          <Field label="Exchange rate (₹ per unit)">
            <input type="number" value={cur.exchange_rate ?? ''} onChange={(e) => setCur('exchange_rate', e.target.value === '' ? null : Number(e.target.value))} className={V_INPUT} data-testid="currency-rate" />
          </Field>
        </div>
        <Button
          type="button" variant="outline" size="sm"
          onClick={fetchRate}
          disabled={fetchingRate}
          className="text-xs mt-3"
          data-testid="fetch-rate-btn"
        >
          <RefreshCw className={`h-3.5 w-3.5 mr-1.5 ${fetchingRate ? 'animate-spin' : ''}`} /> {fetchingRate ? 'Fetching…' : 'Fetch current rate'}
        </Button>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-4">
          <Field label="ATM tips">
            <textarea value={cur.atm_tips || ''} onChange={(e) => setCur('atm_tips', e.target.value)} rows={2} className="w-full bg-transparent border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary-500" data-testid="currency-atm-tips" />
          </Field>
          <Field label="Exchange tips">
            <textarea value={cur.exchange_tips || ''} onChange={(e) => setCur('exchange_tips', e.target.value)} rows={2} className="w-full bg-transparent border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-primary-500" data-testid="currency-exchange-tips" />
          </Field>
        </div>
      </div>

      {/* VOUCHER NOTES */}
      <SectionDivider>Voucher notes</SectionDivider>
      <div data-testid="voucher-notes-section">
        <RichTextEditor
          value={voucherNotes || ''}
          onChange={(html) => setVoucherNotes(html)}
          placeholder="Any additional notes for the traveller's voucher…"
          minHeight={120}
          testId="voucher-notes-editor"
        />
      </div>
    </div>
  );
}
